// lib/form-parsers.ts

import { validate } from "convex-helpers/validators";
import { v } from "convex/values";
import { FieldParser } from "../useFormValidation";
import { Id, TableNames } from "@/convex/_generated/dataModel";
import {
  parsePhone,
  formatPhone,
  validatePhone,
  getPhoneValidationMessage,
  type PhoneState,
} from "./phoneParser";

export const numberParser: FieldParser<string | number, number> = {
  parse: (dbValue: number) => dbValue,
  format: (formValue: string | number) => {
    const num =
      typeof formValue === "string" ? parseFloat(formValue) : formValue;
    return isNaN(num) ? 0 : num;
  },
  validate: (formValue: string | number) => {
    const num =
      typeof formValue === "string" ? parseFloat(formValue) : formValue;
    return !isNaN(num) && validate(v.number(), num, { throw: false });
  },
};

export const phoneParser: FieldParser<PhoneState, string> = {
  parse: (dbValue: string) => parsePhone(dbValue),
  format: (formValue: PhoneState) => formatPhone(formValue) || "",
  validate: (formValue: PhoneState) => validatePhone(formValue),
  getValidationMessage: (formValue: PhoneState) =>
    getPhoneValidationMessage(formValue),
};

export const createNumberParser = (options?: {
  decimals?: number;
  min?: number;
  max?: number;
  defaultValue?: number;
  integer?: boolean;
}): FieldParser<string | number, number> => ({
  parse: (dbValue: number) => dbValue,
  format: (formValue: string | number) => {
    // Si es string vacío o undefined, usar default
    if (formValue === "" || formValue === undefined || formValue === null) {
      return options?.defaultValue ?? 0;
    }

    let num =
      typeof formValue === "string"
        ? options?.integer
          ? parseInt(formValue, 10)
          : parseFloat(formValue)
        : formValue;

    if (isNaN(num)) {
      return options?.defaultValue ?? 0;
    }

    // Aplicar decimales
    if (options?.decimals !== undefined) {
      num = parseFloat(num.toFixed(options.decimals));
    }

    // Aplicar límites
    if (options?.min !== undefined && num < options.min) {
      num = options.min;
    }
    if (options?.max !== undefined && num > options.max) {
      num = options.max;
    }

    return num;
  },
  validate: (formValue: string | number) => {
    // Aceptar strings vacíos si hay defaultValue
    if (
      (formValue === "" || formValue === undefined || formValue === null) &&
      options?.defaultValue !== undefined
    ) {
      return true;
    }

    let num =
      typeof formValue === "string"
        ? options?.integer
          ? parseInt(formValue, 10)
          : parseFloat(formValue)
        : formValue;

    if (isNaN(num)) return false;

    // Validar con Convex
    if (!validate(v.number(), num, { throw: false })) return false;

    // Validar entero si es requerido
    if (options?.integer && !Number.isInteger(num)) return false;

    // Validar límites
    if (options?.min !== undefined && num < options.min) return false;
    if (options?.max !== undefined && num > options.max) return false;

    return true;
  },
  getValidationMessage: (formValue: string | number) => {
    let num =
      typeof formValue === "string"
        ? options?.integer
          ? parseInt(formValue, 10)
          : parseFloat(formValue)
        : formValue;

    if (isNaN(num)) return "Debe ser un número válido";

    if (options?.integer && !Number.isInteger(num)) {
      return "Debe ser un número entero";
    }

    if (options?.min !== undefined && num < options.min) {
      return `Debe ser mayor o igual a ${options.min}`;
    }

    if (options?.max !== undefined && num > options.max) {
      return `Debe ser menor o igual a ${options.max}`;
    }

    return null;
  },
});

export const createIdArrayParser = <T extends TableNames>(
  tableName: T,
): FieldParser<string | Id<T>[], Id<T>[]> => ({
  parse: (dbValue: Id<T>[] | string) => {
    // Si es array, convertir a string separado por comas
    if (Array.isArray(dbValue)) {
      return dbValue.join(",");
    }
    // Si es string, devolverlo tal cual
    if (typeof dbValue === "string") {
      return dbValue;
    }
    // Si es undefined o null, devolver string vacío
    return "";
  },
  format: (formValue: string | Id<T>[]) => {
    if (!formValue) {
      return [];
    }

    // Si es string, convertir a array
    if (typeof formValue === "string") {
      return formValue
        .split(",")
        .filter((id: string) => id.trim() !== "")
        .map((id: string) => id.trim() as Id<T>);
    }

    // Si ya es array, filtrar valores vacíos
    if (Array.isArray(formValue)) {
      return formValue.filter((id: Id<T>) => id.trim() !== "") as Id<T>[];
    }

    // Caso por defecto
    return [];
  },
  validate: (formValue: string | Id<T>[]) => {
    // Validar que sea string o array
    if (typeof formValue === "string") {
      // Si es string, validar que sea un string válido (puede estar vacío)
      return true;
    }
    if (Array.isArray(formValue)) {
      // Si es array, validar que todos los elementos sean strings no vacíos
      return formValue.every(
        (item) => typeof item === "string" && item.trim() !== "",
      );
    }
    return false;
  },
});

export const createBooleanParser = <T extends TableNames>(
  tableName: T,
): FieldParser<boolean, boolean> => ({
  parse: (dbValue: boolean) => {
    return dbValue;
  },
  format: (formValue: boolean) => {
    return formValue;
  },
  validate: (formValue: boolean) => {
    return typeof formValue === "boolean";
  },
});
export const createCheckboxParser = <T extends TableNames, TValue = any>(
  tableName: T,
  trueValue: TValue,
  falseValue: TValue,
): FieldParser<TValue, TValue> => ({
  parse: (dbValue: TValue | any) => {
    // DB → Form
    if (typeof trueValue === "boolean" && typeof dbValue === "string") {
      return (dbValue === "true") as TValue;
    }
    return dbValue;
  },
  format: (formValue: TValue | any) => {
    // Form → DB: convertir string a boolean si es necesario
    if (typeof trueValue === "boolean" && typeof formValue === "string") {
      return (formValue === "true") as TValue;
    }
    return formValue;
  },
  validate: (formValue: TValue | any) => {
    // Convertir string a tipo esperado antes de validar
    let normalizedValue = formValue;

    if (typeof trueValue === "boolean" && typeof formValue === "string") {
      normalizedValue = formValue === "true";
    }

    return normalizedValue === trueValue || normalizedValue === falseValue;
  },
  getValidationMessage: (formValue: TValue | any) => {
    return `El valor debe ser ${JSON.stringify(trueValue)} o ${JSON.stringify(falseValue)}, recibido: ${JSON.stringify(formValue)} (tipo: ${typeof formValue})`;
  },
});

export const createPhoneParser = (options?: {
  required?: boolean;
  defaultCountryCode?: string;
  minDigits?: number;
  maxDigits?: number;
}): FieldParser<PhoneState, string> => ({
  parse: (dbValue: string) => parsePhone(dbValue),
  format: (formValue: PhoneState) => formatPhone(formValue) || "",
  validate: (formValue: PhoneState) => {
    if (!options?.required && (!formValue || !formValue.number?.trim())) {
      return true;
    }

    if (options?.required && (!formValue || !formValue.number?.trim())) {
      return false;
    }

    const cleanNumber = formValue.number.replace(/[\s\-\(\)]/g, "");

    // Remove any non-digit characters including invisible unicode characters
    const digitsOnly = cleanNumber.replace(/\D/g, "");

    if (!/^\d+$/.test(digitsOnly)) {
      return false;
    }

    const length = cleanNumber.length;
    const minDigits = options?.minDigits ?? 6;
    const maxDigits = options?.maxDigits ?? 15;

    return length >= minDigits && length <= maxDigits;
  },
  getValidationMessage: (formValue: PhoneState) => {
    if (!options?.required && (!formValue || !formValue.number?.trim())) {
      return null;
    }

    if (options?.required && (!formValue || !formValue.number?.trim())) {
      return "El número de teléfono es requerido";
    }

    const cleanNumber = formValue.number.replace(/[\s\-\(\)]/g, "");

    if (!/^\d+$/.test(cleanNumber)) {
      return "El número solo debe contener dígitos";
    }

    const length = cleanNumber.length;
    const minDigits = options?.minDigits ?? 6;
    const maxDigits = options?.maxDigits ?? 15;

    if (length < minDigits) {
      return `El número es muy corto (mínimo ${minDigits} dígitos)`;
    }
    if (length > maxDigits) {
      return `El número es muy largo (máximo ${maxDigits} dígitos)`;
    }

    return null;
  },
});
